import { type ChartDateRange, Periodicity } from "@bloomberg/octagon-shared";
import {
  ONE_MINUTE_IN_MILLISECONDS,
  ON_HOUR_IN_MILLISECONDS,
} from "~constants";
import type { TickerChartDateRange } from "~types/ticker-chart";
import { DataInterval } from "~types/types";

type IntradayChartConfig = {
  source: "intraday";
  intervalMinutes: number;
  dataInterval: DataInterval;
  staleTime: number;
  tradingDayCount?: number;
};

type HistoryChartConfig = {
  source: "history";
  periodicity: Periodicity;
  dataInterval: DataInterval;
  staleTime: number;
};

export type ChartConfig = IntradayChartConfig | HistoryChartConfig;

type ChartVariant = "line" | "ohlcv";

type IntradayBase = {
  intervalMinutes: number;
  dataInterval: DataInterval;
  tradingDayCount: number;
};

const INTRADAY_CONFIGS: Record<string, Record<ChartVariant, IntradayBase>> = {
  "1H": {
    line: { intervalMinutes: 1, dataInterval: "1m", tradingDayCount: 1 },
    ohlcv: { intervalMinutes: 1, dataInterval: "1m", tradingDayCount: 1 },
  },
  "1D": {
    line: { intervalMinutes: 5, dataInterval: "5m", tradingDayCount: 1 },
    ohlcv: { intervalMinutes: 15, dataInterval: "15m", tradingDayCount: 1 },
  },
  "3D": {
    line: { intervalMinutes: 30, dataInterval: "30m", tradingDayCount: 3 },
    ohlcv: { intervalMinutes: 60, dataInterval: "1h", tradingDayCount: 3 },
  },
  "5D_1W": {
    line: { intervalMinutes: 60, dataInterval: "1h", tradingDayCount: 5 },
    ohlcv: { intervalMinutes: 120, dataInterval: "2h", tradingDayCount: 5 },
  },
};

function resolveYTDRange(ytdDays: number): Exclude<ChartDateRange, "YTD"> {
  if (ytdDays <= 1) return "1D";
  if (ytdDays <= 3) return "3D";
  if (ytdDays <= 5) return "5D";
  if (ytdDays <= 7) return "1W";
  if (ytdDays <= 120) return "1M";
  if (ytdDays <= 180) return "6M";

  return "1Y";
}

export function getYTDInDays(): number {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const diffInMs = now.getTime() - startOfYear.getTime();

  return diffInMs / (1000 * 60 * 60 * 24);
}

export function getChartConfig(
  chartDateRange: TickerChartDateRange,
  isOhlcv: boolean,
  ytdDays = 0
): ChartConfig {
  const variant: ChartVariant = isOhlcv ? "ohlcv" : "line";
  const effectiveRange =
    chartDateRange === "YTD" ? resolveYTDRange(ytdDays) : chartDateRange;

  switch (effectiveRange) {
    case "1H":
      return {
        source: "intraday",
        ...INTRADAY_CONFIGS["1H"][variant],
        staleTime: ONE_MINUTE_IN_MILLISECONDS,
      };

    case "1D":
      return {
        source: "intraday",
        ...INTRADAY_CONFIGS["1D"][variant],
        staleTime: ONE_MINUTE_IN_MILLISECONDS * 5,
      };

    case "3D":
      return {
        source: "intraday",
        ...INTRADAY_CONFIGS["3D"][variant],
        staleTime: ONE_MINUTE_IN_MILLISECONDS * 30,
      };

    case "5D":
    case "1W":
      return {
        source: "intraday",
        ...INTRADAY_CONFIGS["5D_1W"][variant],
        staleTime: ON_HOUR_IN_MILLISECONDS,
      };

    case "1M":
      return {
        source: "history",
        periodicity: Periodicity.Daily,
        dataInterval: "1d",
        staleTime: ON_HOUR_IN_MILLISECONDS * 8,
      };

    case "6M":
      return {
        source: "history",
        periodicity: Periodicity.Weekly,
        dataInterval: "1w",
        staleTime: ON_HOUR_IN_MILLISECONDS * 12,
      };

    case "1Y":
      return {
        source: "history",
        periodicity: Periodicity.Weekly,
        dataInterval: "1w",
        staleTime: ON_HOUR_IN_MILLISECONDS * 24,
      };

    case "5Y":
      return {
        source: "history",
        periodicity: Periodicity.Monthly,
        dataInterval: "1M",
        staleTime: ON_HOUR_IN_MILLISECONDS * 24,
      };

    default:
      throw new Error(`Unhandled chart date range: ${String(effectiveRange)}`);
  }
}
